/**
 * Engineering-doc short ids (`tdd-0096`), as they appear in URLs.
 *
 * Client-safe — no DB import — so the editor page can tell a short-id URL from
 * a platform nanoid before it has fetched anything. The resolver itself lives in
 * `resolve-doc.ts`.
 *
 * People type these from memory, and the corpus is not consistent about padding
 * either (`tdd-0096` but `rfc-002`), so `/tdd-96`, `/tdd-096` and `/TDD-0096`
 * must all land on the same document. Matching is therefore by prefix and
 * *numeric value*, never by string.
 */

const SHORT_ID = /^([a-z]{2,4})-([0-9]{1,5})$/i;

/** Whether an identifier looks like an engineering-doc id rather than a nanoid. */
export function looksLikeShortId(id: string): boolean {
  return SHORT_ID.test(id);
}

/** `TDD-096` → `{ prefix: "tdd", num: 96 }`; `null` for anything else. */
export function parseShortId(id: string): { prefix: string; num: number } | null {
  const m = SHORT_ID.exec(id);
  if (!m) return null;
  return { prefix: m[1]!.toLowerCase(), num: Number.parseInt(m[2]!, 10) };
}

/**
 * The short id in a document's stored frontmatter JSON (lowercased, as
 * written — padding is kept), or `null` when there is none or it is not an
 * engineering-doc id.
 */
export function shortIdFromFrontmatter(frontmatter: string | null | undefined): string | null {
  if (!frontmatter) return null;
  try {
    const id = (JSON.parse(frontmatter) as { id?: unknown }).id;
    return typeof id === "string" && looksLikeShortId(id) ? id.toLowerCase() : null;
  } catch {
    return null;
  }
}

/** Whether two short ids name the same document, ignoring case and padding. */
export function sameShortId(a: string, b: string): boolean {
  const pa = parseShortId(a);
  const pb = parseShortId(b);
  return !!pa && !!pb && pa.prefix === pb.prefix && pa.num === pb.num;
}
